import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../ContractSearch.styles";
import t from "../ContractSearch.tokens";
import { DocGlyph, SendGlyph } from "./ContractSearchIcons";

interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  /** No documents to search, or a search already running. */
  disabled?: boolean;
  isSending?: boolean;
  /** Set once to move focus into the box (e.g. after "New search" or a dashboard prompt). */
  autoFocus?: boolean;
}

/**
 * The question box. Enter sends, Shift+Enter inserts a newline (the hint under
 * it says so). Grows with its content up to `maxRows`, then scrolls.
 */
const Composer = ({
  value,
  onChange,
  onSend,
  disabled = false,
  isSending = false,
  autoFocus = false,
}: ComposerProps) => {
  const copy = strings.contractSearch;
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const canSend = !disabled && !isSending && value.trim().length > 0;

  // Auto-grow: reset, then take the scroll height up to the row cap.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = Math.round(parseFloat(t.composer.fontSize) * t.composer.lineHeight);
    const max = lineHeight * t.composer.maxRows + 2 * parseFloat(t.composer.inputPaddingY);
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [value]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const submit = () => {
    if (!canSend) return;
    onSend(value.trim());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Box>
      <Box
        component="form"
        sx={styles.composer(focused)}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Box
          component="textarea"
          ref={inputRef}
          rows={1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={copy.composerPlaceholder}
          aria-label={copy.composerLabel}
          maxLength={copy.maxQueryLength}
          disabled={disabled}
          sx={styles.composerInput}
        />
        <Box
          component="button"
          type="submit"
          disabled={!canSend}
          aria-label={copy.send}
          sx={styles.sendButton}
        >
          <SendGlyph sx={{ width: t.composer.sendIconSize, height: t.composer.sendIconSize }} />
        </Box>
      </Box>
      <Box sx={styles.composerHint}>
        <Typography component="span" sx={styles.composerNote}>
          <DocGlyph sx={{ width: t.composer.noteIconSize, height: t.composer.noteIconSize, flexShrink: 0 }} />
          {copy.composerNote}
        </Typography>
        <Typography
          component="span"
          sx={{ ...styles.composerHintText, display: { xs: "none", sm: "inline" } }}
        >
          {copy.composerHint}
        </Typography>
      </Box>
    </Box>
  );
};

export default Composer;
